import React, { useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Home, Users, GraduationCap, Calendar, Settings, LogOut,
  Bell, Search, Plus, ChevronDown, Target, Sparkles, Zap,
  Lightbulb, Package, MessageSquare,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import NotificationDropdown from '../notifications/NotificationDropdown';

const DashboardLayout = ({ children }) => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Embed mode: gdy Aurabase jest osadzona w iframe Sorto-CRM (lub innego hosta),
  // pokazujemy tylko content — bez sidebar/header.
  // Trigger: ?embed=1 w URL LUB fakt że jesteśmy w iframe (window.parent !== window.self).
  // sessionStorage używany tylko jako fallback gdy w iframe (preserve przy navigacji
  // bez query). NIE używamy sessionStorage gdy standalone — żeby raz-w-iframe użytkownik
  // przy powrocie na bezpośredni URL widział pełny layout.
  const embedQuery = searchParams.get('embed') === '1';
  const inIframe = typeof window !== 'undefined' && window.self !== window.parent;
  if (embedQuery && inIframe && typeof window !== 'undefined') {
    try { sessionStorage.setItem('aurabase_embed', '1'); } catch {}
  } else if (!inIframe && typeof window !== 'undefined') {
    // Standalone visit — cichno wyczyść leftover z poprzednich iframe sesji
    try { sessionStorage.removeItem('aurabase_embed'); } catch {}
  }
  const isEmbed = (embedQuery && inIframe) || (inIframe && typeof window !== 'undefined' && sessionStorage.getItem('aurabase_embed') === '1');

  const sidebarLinks = [
    { icon: Home, key: 'dashboard', href: '/dashboard' },
    { icon: Users, key: 'communities', href: '/dashboard/communities' },
    { icon: MessageSquare, key: 'messages', href: '/dashboard/messages' },
    { icon: GraduationCap, key: 'courses', href: '/dashboard/courses' },
    { icon: Calendar, key: 'events', href: '/dashboard/events' },
    { icon: Target, key: 'quests', href: '/dashboard/quests', badge: true },
    { icon: Sparkles, key: 'connections', href: '/dashboard/connections' },
    { icon: Lightbulb, key: 'roadmap', href: '/dashboard/roadmap' },
    { icon: Package, key: 'marketplace', href: '/dashboard/marketplace' },
    { icon: Settings, key: 'settings', href: '/dashboard/settings' },
  ];

  const [createOpen, setCreateOpen] = useState(false);

  const switchLang = (lang) => {
    i18n.changeLanguage(lang);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (isEmbed) {
    // Embed: tylko content, full-bleed. Host (CRM) ma własny sidebar/header.
    return (
      <div className="min-h-screen bg-gray-50">
        <main className="p-4 sm:p-6">{children}</main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-gray-200 z-40 flex flex-col">
        {/* Logo */}
        <div className="h-16 flex items-center px-6 border-b border-gray-100 flex-shrink-0">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <span className="text-white font-bold text-lg">A</span>
            </div>
            <span className="text-gray-900 font-semibold text-xl tracking-tight">AuraBase</span>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1 flex-1 overflow-y-auto min-h-0">
          {sidebarLinks.map((link) => {
            const isActive = location.pathname === link.href;
            return (
              <Link
                key={link.href}
                to={link.href}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                  isActive
                    ? 'bg-emerald-50 text-emerald-600'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <link.icon className="w-5 h-5" />
                <span className="font-medium">{t(`nav.${link.key}`)}</span>
                {link.badge && (
                  <span className="ml-auto px-2 py-0.5 text-xs bg-emerald-100 text-emerald-700 rounded-full">
                    {t('nav.new_badge')}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-gray-100 flex-shrink-0">
          {/* Aura Score mini */}
          {user?.aura_score !== undefined && (
            <div className="flex items-center gap-2 mb-3 px-2">
              <Zap className="w-4 h-4 text-emerald-500" />
              <span className="text-sm text-gray-600">{t('nav.aura_score')}:</span>
              <span className="text-sm font-semibold text-emerald-600">{user.aura_score}</span>
            </div>
          )}
          {/* Language switcher */}
          <div className="flex gap-1 mb-3 px-2 text-xs">
            <button
              onClick={() => switchLang('pl')}
              className={`px-2 py-0.5 rounded ${i18n.language?.startsWith('pl') ? 'bg-emerald-100 text-emerald-700 font-semibold' : 'text-gray-500 hover:text-gray-700'}`}
            >
              PL
            </button>
            <button
              onClick={() => switchLang('en')}
              className={`px-2 py-0.5 rounded ${i18n.language?.startsWith('en') ? 'bg-emerald-100 text-emerald-700 font-semibold' : 'text-gray-500 hover:text-gray-700'}`}
            >
              EN
            </button>
          </div>
          <div className="flex items-center gap-3 mb-4">
            <img
              src={user?.avatar || 'https://ui-avatars.com/api/?name=User&background=10b981&color=fff'}
              alt={user?.name}
              className="w-10 h-10 rounded-full"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button variant="outline" className="w-full" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />
            {t('nav.logout')}
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <div className="ml-64">
        {/* Top header */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 sticky top-0 z-30">
          <div className="flex items-center gap-4 flex-1">
            <form
              className="relative max-w-md flex-1"
              onSubmit={(e) => {
                e.preventDefault();
                const q = new FormData(e.currentTarget).get('q')?.toString().trim();
                if (q && q.length >= 2) navigate(`/dashboard/search?q=${encodeURIComponent(q)}`);
              }}
            >
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                name="q"
                type="text"
                placeholder={t('common.search')}
                className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </form>
          </div>

          <div className="flex items-center gap-4">
            <NotificationDropdown />

            <div className="relative">
              <Button size="sm" onClick={() => setCreateOpen((o) => !o)}>
                <Plus className="w-4 h-4 mr-2" />
                {t('common.create')}
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
              {createOpen && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setCreateOpen(false)} />
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-40">
                    {[
                      { key: 'communities', href: '/dashboard/communities' },
                      { key: 'courses', href: '/dashboard/courses' },
                      { key: 'events', href: '/dashboard/events' },
                    ].map((it) => (
                      <button
                        key={it.key}
                        onClick={() => { setCreateOpen(false); navigate(it.href); }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        {t(`nav.${it.key}`)}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
